/**
 * Firebase instance shim — single source of truth for Firestore/Auth/Functions.
 *
 * Each surface (mobile, web) initializes Firebase its own way (RN persistence vs
 * browser persistence, App Check, etc.) and then calls `setFirebase(...)` once
 * at bootstrap. Shared code reads via `getDb()`, `getAuth()`, `getFunctions()`
 * instead of importing from a surface-specific firebase config file.
 *
 * Why this exists: 60+ files in packages/shared/services/ historically imported
 * mobile's `src/config/firebase.ts` directly, which broke when web tried to use
 * them. This shim lets shared code stay surface-agnostic.
 *
 * Bootstrap order rule: every consumer of `getDb()` must run AFTER `setFirebase`
 * has been called. Surfaces should call it synchronously at module load —
 * mobile in `src/config/firebase.ts` after `initFirebase(...)`; web in
 * `packages/web/src/lib/firebase.ts` after `initializeApp(...)`.
 */

import type { Auth } from "firebase/auth";
import type { Firestore } from "firebase/firestore";
import type { Functions } from "firebase/functions";
import type { FirebaseApp } from "firebase/app";

interface FirebaseRefs {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
}

let _refs: FirebaseRefs | null = null;

export function setFirebase(refs: FirebaseRefs): void {
  _refs = refs;
}

function assertReady(): FirebaseRefs {
  if (!_refs) {
    throw new Error(
      "[shared/firebase] setFirebase() has not been called yet. " +
        "Mobile must call it from src/config/firebase.ts; web must call it from packages/web/src/lib/firebase.ts."
    );
  }
  return _refs;
}

export function getApp(): FirebaseApp {
  return assertReady().app;
}

export function getDb(): Firestore {
  return assertReady().db;
}

export function getAuth(): Auth {
  return assertReady().auth;
}

export function getFunctions(): Functions {
  return assertReady().functions;
}

/** True iff `setFirebase` has been called. Useful for guards in tests. */
export function isFirebaseReady(): boolean {
  return _refs !== null;
}
